import { Language } from '~common/i18n';
import {
  arrayTranslations,
  mergeTranslations,
} from '~admin/view/components/create-event/helper/merge-translations.helper';
import { SuggestedIncrementsFormData } from '~admin/view/components/details/offer-group-details/offer-group-suggested-increments.component';
import { OfferGroupFormData } from '~admin/view/components/details/offer-group-details/offer-group-fields.component';
import { InventoryReference } from '~client/model/admin/inventory-reference';
import { InventoryReferenceCodeType } from '~client/model/admin/inventory-reference-code-type';
import {
  CreateOfferGroupRequest,
  OfferGroupPriceRequest,
} from '~client/model/admin/create-offer-group-request';
import {
  OfferGroupTranslationRequestItem,
  OfferGroupTranslationsRequest,
  SuggestedOfferIncrementTranslationRequest,
  SuggestedOfferIncrementTranslationRequestItem,
} from '~client/model/admin/offer-group-translations-request';
import { SuggestedOfferIncrementRequest } from '~client/model/admin/suggested-offer-increment-request';
import { isNilOrEmpty } from '~common/helper/global';
import { datetime } from '~common/helper/datetime';
import { isGreaterThanZero } from '~common/validator/validation-rules';

/**
 * Transforms OfferGroupFormData to OfferGroupRequest
 * @param marketplaceId
 * @param parentId
 * @param values
 */
export const getOfferGroupRequest = (
  marketplaceId: number,
  parentId: number,
  values: OfferGroupFormData,
): CreateOfferGroupRequest => {
  const {
    name,
    prices,
    additionalDetails,
    capacityDefaultString,
    seatConfigurationType,
    sections,
    suggestedIncrements,
  } = values;

  const priceRequests: OfferGroupPriceRequest[] = prices.map(
    ({ price, startDateTime, endDateTime }): any => ({
      // Absurd price is the amount the server would reject if a user tries to pay much more
      // than the reserve price. This is generally set to 20 times the reserve price, unless
      // the base Yoop is offered for Free. In this case, default the price to a $100.
      absurdPrice: isGreaterThanZero(price) ? price * 20 : 100,
      endDate: datetime.parse(endDateTime)?.toISOString(),
      historicalAveragePrice: price,
      reservePrice: price,
      startDate: datetime.parse(startDateTime)?.toISOString(),
    }),
  );

  const inventoryReferences: InventoryReference[] = sections.map(
    (sectionCode): InventoryReference => ({
      code: sectionCode.toString(),
      codeType: InventoryReferenceCodeType.SECTION,
    }),
  );

  const suggestedOfferIncrements: SuggestedOfferIncrementRequest[] = suggestedIncrements?.map(
    (suggestedIncrement): SuggestedOfferIncrementRequest => ({
      title: suggestedIncrement.title[0].value,
      value: suggestedIncrement.value,
    }),
  );

  return {
    additionalDetails: additionalDetails[0].value,
    capacityDefaultString: capacityDefaultString[0].value,
    marketplaceId,
    parentId,
    description: name[0].value,
    seatConfigurationType,
    priceRequests,
    inventoryReferences,
    suggestedOfferIncrements,
  };
};

/**
 * Transforms OfferGroupFormData[] to OfferGroupRequest[]
 * @param marketplaceId
 * @param parentId
 * @param valuesList
 */
export const getOfferGroupRequests = (
  marketplaceId: number,
  parentId: number,
  valuesList: OfferGroupFormData[],
): CreateOfferGroupRequest[] => {
  return valuesList.map((values) => getOfferGroupRequest(marketplaceId, parentId, values));
};

/**
 * Transforms SuggestedIncrementsFormData[] to SuggestedOfferIncrementTranslationRequest[]
 * @param suggestedOfferIncrementIds
 * @param suggestedIncrements
 */
const getSuggestedOfferIncrementTranslationRequest = (
  suggestedOfferIncrementIds: number[],
  suggestedIncrements: SuggestedIncrementsFormData[],
): SuggestedOfferIncrementTranslationRequest[] => {
  if (isNilOrEmpty(suggestedOfferIncrementIds)) {
    return null;
  }

  return suggestedOfferIncrementIds.map(
    (id, index): SuggestedOfferIncrementTranslationRequest => {
      const suggestedIncrementTranslations = new Map<
        Language,
        SuggestedOfferIncrementTranslationRequestItem
      >();
      mergeTranslations(suggestedIncrementTranslations, 'title', suggestedIncrements[index].title);

      return {
        suggestedOfferIncrementId: id,
        suggestedOfferIncrementTranslationRequestItemList: arrayTranslations(
          suggestedIncrementTranslations,
        ),
      };
    },
  );
};

/**
 * Transforms OfferGroupFormData to OfferGroupTranslationsRequest
 * @param offerGroupId
 * @param suggestedOfferIncrementIds
 * @param values
 */
export const getOfferGroupTranslationsRequest = (
  offerGroupId: number,
  suggestedOfferIncrementIds: number[],
  values: OfferGroupFormData,
): OfferGroupTranslationsRequest => {
  const { name, additionalDetails, capacityDefaultString, suggestedIncrements } = values;

  const offerGroupNameTranslations = new Map<Language, OfferGroupTranslationRequestItem>();
  mergeTranslations(offerGroupNameTranslations, 'description', name);
  mergeTranslations(offerGroupNameTranslations, 'additionalDetails', additionalDetails);
  mergeTranslations(offerGroupNameTranslations, 'capacityDefaultString', capacityDefaultString);

  return {
    offerGroupId: offerGroupId.toString(),
    offerGroupTranslationRequestItemList: arrayTranslations(offerGroupNameTranslations),
    suggestedOfferIncrementTranslationRequestList: getSuggestedOfferIncrementTranslationRequest(
      suggestedOfferIncrementIds,
      suggestedIncrements,
    ),
  };
};



// WEBPACK FOOTER //
// ./view/components/create-event/helper/offer-group-request.helper.ts